/**
 * V13.5 — Recherche globale AITO.PF
 *
 * La loupe du header pointe vers cette page. Elle interroge en parallèle
 * tous les modules "cherchables" du site et regroupe les résultats par
 * catégorie. Chaque résultat renvoie vers sa fiche/dédiée respective.
 *
 * Modules couverts :
 *   - Athlètes (/athletes)
 *   - Coachs & Encadrants (/coachs)
 *   - Clubs (/clubs)
 *   - Fédérations (/federations)
 *   - Actualités (/actualites)
 *   - Compétitions (/competitions)
 *   - Mémoire du sport (/memoire)
 *   - Annuaire Santé & Boutiques (/annuaire/…)
 *
 * L'algorithme est simple : filtrage côté client sur les listes publiques
 * (title/name/slug/description) avec un fallback serveur `q` quand supporté.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Search, Loader2, Users, ShieldCheck, Building2, Flag, Newspaper,
  Trophy, BookOpen, HeartPulse, Store,
} from "lucide-react";
import { AthleteCard } from "@/components/AthleteCard";
import { NewsCard } from "@/components/NewsCard";
import { resolveAthletePhoto } from "@/lib/photoFallback";

// Normalise pour comparaison insensible aux accents/casse
function norm(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function matches(item, q) {
  if (!q) return true;
  const nq = norm(q);
  const haystack = [
    item.full_name, item.name, item.title, item.slug,
    item.description, item.description_short, item.summary,
    item.bio_short, item.author, item.source_name,
    item.commune, item.island, item.discipline,
    (item.disciplines || []).join(" "),
    (item.tags || []).join(" "),
  ].filter(Boolean).map(norm).join(" ");
  return haystack.includes(nq);
}

const SECTIONS = [
  { key: "athletes",    label: "Athlètes",              icon: Users,        endpoint: "/athletes",                  to: (i) => `/athletes/${i.slug || i.id}` },
  { key: "coaches",     label: "Coachs & Encadrants",   icon: ShieldCheck,  endpoint: "/coaches",                   to: (i) => `/coachs/${i.slug || i.id}` },
  { key: "clubs",       label: "Clubs",                 icon: Building2,    endpoint: "/clubs",                     to: (i) => `/clubs/${i.slug || i.id}` },
  { key: "federations", label: "Fédérations",           icon: Flag,         endpoint: "/federations",               to: (i) => `/federations/${i.slug || i.id}` },
  { key: "news",        label: "Actualités",            icon: Newspaper,    endpoint: "/news",                      to: (i) => `/actualites/${i.slug || i.id}` },
  { key: "competitions", label: "Compétitions",         icon: Trophy,       endpoint: "/competitions",              to: (i) => `/competitions/${i.slug || i.id}` },
  { key: "memoire",     label: "Mémoire du sport",      icon: BookOpen,     endpoint: "/sport-memory",              to: (i) => `/memoire/${i.slug || i.id}` },
  { key: "sante",       label: "Santé & Performance",   icon: HeartPulse,   endpoint: "/directory?type=sante",      to: (i) => `/annuaire/sante-performance/${i.slug || i.id}` },
  { key: "boutiques",   label: "Boutiques & Services",  icon: Store,        endpoint: "/directory?type=boutique",   to: (i) => `/annuaire/boutiques-services/${i.slug || i.id}` },
];

function extractItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.items)) return payload.items;
  return [];
}

function titleOf(item, section) {
  return item.full_name || item.name || item.title || item.slug || "Sans titre";
}

function subtitleOf(item, section) {
  const bits = [];
  if (section.key === "athletes" || section.key === "coaches") {
    if (item.main_discipline) bits.push(item.main_discipline);
    else if ((item.disciplines || []).length) bits.push((item.disciplines || []).slice(0, 2).join(", "));
    if (item.commune || item.island) bits.push([item.commune, item.island].filter(Boolean).join(" · "));
  } else if (section.key === "clubs" || section.key === "federations") {
    if (item.discipline) bits.push(item.discipline);
    if (item.commune || item.island) bits.push([item.commune, item.island].filter(Boolean).join(" · "));
  } else if (section.key === "memoire") {
    if (item.author) bits.push(item.author);
    if (item.publication_year) bits.push(String(item.publication_year));
    else if (item.year_start) bits.push(String(item.year_start));
    if (item.content_type) bits.push(String(item.content_type).replace(/_/g, " "));
  } else if (section.key === "news") {
    if (item.source_name || item.source) bits.push(item.source_name || item.source);
    if (item.published_at) bits.push(new Date(item.published_at).toLocaleDateString("fr-FR"));
  } else if (section.key === "sante" || section.key === "boutiques") {
    if (item.subcategory) bits.push(String(item.subcategory).replace(/_/g, " "));
    if (item.commune || item.island) bits.push([item.commune, item.island].filter(Boolean).join(" · "));
  } else if (section.key === "competitions") {
    if (item.discipline) bits.push(item.discipline);
    if (item.date_start) bits.push(new Date(item.date_start).toLocaleDateString("fr-FR"));
  }
  return bits.filter(Boolean).join(" · ");
}

function imgOf(item, section) {
  if (section.key === "athletes" || section.key === "coaches") return resolveAthletePhoto(item);
  return item.photo_url || item.logo_url || item.image_url || item.cover_url || null;
}

export default function SearchPage() {
  const [q, setQ] = useState("");
  const [buffers, setBuffers] = useState({}); // { athletes: [...], news: [...] }
  const [loading, setLoading] = useState(false);

  // Charge un ÉCHANTILLON LARGE pour chaque module dès la première recherche
  // (les modules AITO restent de petite taille, ~quelques centaines d'items).
  useEffect(() => {
    if (!q || Object.keys(buffers).length > 0) return;
    setLoading(true);
    Promise.all(SECTIONS.map(async (s) => {
      try {
        const res = await api.get(s.endpoint, { params: { limit: 200, q } });
        return [s.key, extractItems(res.data)];
      } catch {
        return [s.key, []];
      }
    })).then((entries) => {
      setBuffers(Object.fromEntries(entries));
      setLoading(false);
    });
  }, [q, buffers]);

  const results = useMemo(() => {
    if (!q) return {};
    const out = {};
    for (const s of SECTIONS) {
      const list = buffers[s.key] || [];
      out[s.key] = list.filter((i) => matches(i, q)).slice(0, 12);
    }
    return out;
  }, [q, buffers]);

  const totalCount = Object.values(results).reduce((n, arr) => n + arr.length, 0);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 py-8 sm:py-10" data-testid="search-page">
      <div className="relative">
        <Search className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
        <Input
          data-testid="search-input"
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher partout : athlète, coach, club, actualité, mémoire, boutique…"
          className="pl-9 h-12 text-base"
        />
      </div>

      {!q && (
        <p className="mt-4 text-sm text-muted-foreground">
          Tape un mot-clé pour chercher dans tout AITO.PF (athlètes, coachs, clubs, fédérations, actualités,
          compétitions, mémoire du sport, annuaires santé & boutiques…).
        </p>
      )}

      {q && loading && (
        <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Recherche en cours…
        </div>
      )}

      {q && !loading && (
        <>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <Badge variant="secondary" data-testid="search-total-count">
              {totalCount} résultat{totalCount > 1 ? "s" : ""}
            </Badge>
            {SECTIONS.map((s) => {
              const n = (results[s.key] || []).length;
              if (!n) return null;
              return (
                <a key={s.key} href={`#section-${s.key}`} className="text-xs px-2 py-1 rounded-full bg-muted hover:bg-muted/70">
                  <s.icon className="inline h-3 w-3 mr-1" />
                  {s.label} · {n}
                </a>
              );
            })}
          </div>

          {totalCount === 0 && (
            <div className="mt-10 text-center text-muted-foreground">
              <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Aucun résultat pour « {q} ».</p>
              <p className="text-xs mt-1">Essaie un mot-clé plus court ou différent.</p>
            </div>
          )}

          <div className="mt-6 space-y-8">
            {SECTIONS.map((s) => {
              const items = results[s.key] || [];
              if (!items.length) return null;
              const Icon = s.icon;
              return (
                <section key={s.key} id={`section-${s.key}`} data-testid={`section-${s.key}`}>
                  <h2 className="font-display font-semibold mb-3 flex items-center gap-2 text-lg">
                    <Icon className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" />
                    {s.label}
                    <span className="text-sm text-muted-foreground font-normal">· {items.length}</span>
                  </h2>

                  {/* Athlètes → carte visuelle existante ; actualités → carte existante ; autres → tuile compacte */}
                  {s.key === "athletes" ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {items.map((a) => <AthleteCard key={a.id} athlete={a} />)}
                    </div>
                  ) : s.key === "news" ? (
                    <div className="grid sm:grid-cols-2 gap-3">
                      {items.map((n) => <NewsCard key={n.id} item={n} />)}
                    </div>
                  ) : (
                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      {items.map((it) => (
                        <Link
                          key={it.id || it.slug}
                          to={s.to(it)}
                          className="block group"
                          data-testid={`search-result-${s.key}-${it.slug || it.id}`}
                        >
                          <Card className="hover:border-[hsl(var(--aito-lagoon))]/50 transition-colors">
                            <CardContent className="p-3 flex items-center gap-3">
                              {imgOf(it, s) ? (
                                <img
                                  src={imgOf(it, s)}
                                  alt=""
                                  className="h-14 w-14 rounded-md object-cover shrink-0 border"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="h-14 w-14 rounded-md bg-muted flex items-center justify-center shrink-0">
                                  <Icon className="h-5 w-5 text-muted-foreground/60" />
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-sm truncate group-hover:text-[hsl(var(--aito-lagoon))]">
                                  {titleOf(it, s)}
                                </p>
                                {subtitleOf(it, s) && (
                                  <p className="text-xs text-muted-foreground truncate mt-0.5">
                                    {subtitleOf(it, s)}
                                  </p>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        </Link>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
